import type { CheckoutSdk } from '@athena-lib/Checkout/utils';
import {
  type CartCommandResponse,
  compareSdkVersions,
  type CountriesResponse,
  createCheckoutInstance,
  type StripeElementLocale,
} from '@stickerapp-org/checkout-sdk';
import type { TranslationPairs } from '@athena-lib/ProductWizard/types';
import { Type } from '@sinclair/typebox';
import type { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
import * as colors from '@stickerapp-org/tailwind-config/colors';
import { getContext, hasContext, setContext } from 'svelte';
import type { LocalizationContext } from '@athena-lib/i18n';
import { attempt } from '@athena-lib/attempt';
import { setGlobalError } from '../components/GlobalSdkError.svelte';
import { ensureLeadingSlash, ensureNoTrailingSlash } from '../components/shared/url-utils';
import type { ExpandedCheckoutSettings } from './server/expanders/checkoutSettingsExpander';
import { getText, post } from './http';
import { loadCheckoutWithDefaults } from './utilities/checkout';
import { getCookieFromDocument } from './utilities/getCookieFromDocument';
import { getEnhancedCommereceEventsTracker } from '$lib/gtmEventHelper.svelte.js';
import { goto } from '$app/navigation';

const defaultCheckoutIdCookieKey = 'checkout-cart-id';
const defaultCheckoutVersionCookieKey = 'checkout-cart-version';
export const checkoutVersionSessionKey = 'checkout-version';
export type CheckoutVersion = '1' | '2';

type CheckoutContext = {
  sdk: null | CheckoutSdk;
  settings?: ExpandedCheckoutSettings;
  suggestedPostalCode?: string;
  countries?: CountriesResponse;
  translators?: {
    translateLaminate: (product: string) => string;
    translateMaterial: (material: string) => string;
    translateProductName: (productName: string) => string;
    translateCutDirection: (direction: string) => string;
    translateShape: (path: string) => string;
  };
};

const checkoutContextStorage = $state<{ default: CheckoutContext; [key: string]: CheckoutContext }>(
  {
    default: {
      sdk: null,
    },
  },
);

const currentCheckoutContextKey = 'current-checkout-context-key';

/**
  Gets the initialized instance of checkout sdk, should only be @null if checkoutSdk has
  not been initialized.
 */
export const getCheckoutSdk = () => {
  return getCheckoutContext().sdk ?? null;
};
export const getCheckoutContext = (): CheckoutContext => {
  const key = attempt(() => getContext<string | undefined>(currentCheckoutContextKey));

  if (!key.success) {
    console.warn(
      "couldn't get checkout context key, did you try to get the context in a callback?",
      key.error,
    );
  }

  return checkoutContextStorage[key.value ?? 'default'];
};

export const setCheckoutState = (key: string, newCheckout: CheckoutContext) => {
  if (!(key in checkoutContextStorage)) {
    checkoutContextStorage[key] = { sdk: null };
  }
  setContext(currentCheckoutContextKey, key);
  checkoutContextStorage[key] = {
    ...checkoutContextStorage[key],
    ...newCheckout,
  };

  const translationPairs = newCheckout.settings?.translationPairs;
  if (translationPairs) {
    getCheckoutContext().translators = {
      translateMaterial: createMaterialTranslator(translationPairs),
      translateLaminate: createLaminateTranslator(translationPairs),
      translateProductName: createProductNameTranslator(translationPairs),
      translateCutDirection: createCutDirectionTranslator(translationPairs),
      translateShape: createShapeTranslator(translationPairs),
    };
  }

  let countriesResult: CountriesResponse | undefined;
  newCheckout.sdk?.countries().then((x) => {
    countriesResult = x;
  });
  checkoutContextStorage[key] = {
    ...checkoutContextStorage[key],
    get countries() {
      return countriesResult;
    },
  };
};

export const getCheckoutCountries = () => getCheckoutContext().countries;

export const createMaterialTranslator =
  (translationPairs: TranslationPairs) => (material: string) => {
    const key = `products_material_${material}`;
    return translationPairs?.[key] ?? key;
  };

export const createLaminateTranslator =
  (translationPairs: TranslationPairs) => (laminate: string) => {
    const key = `products_laminate_${laminate}`;
    return translationPairs?.[key] ?? key;
  };

export const createProductNameTranslator =
  (translationPairs: TranslationPairs) => (productName: string) => {
    const key = `products_product_name_${productName}`;
    return translationPairs?.[key] ?? key;
  };

export const createCutDirectionTranslator =
  (translationPairs: TranslationPairs) => (direction: string) => {
    const key = `products_cutdirection_${direction}`;
    return translationPairs?.[key] ?? key;
  };

export const createShapeTranslator = (translationPairs: TranslationPairs) => (path: string) => {
  const key = `products_sheet_name_${path}`;
  return translationPairs?.[key] ?? key;
};

export const getProductsTranslators = () => getCheckoutContext().translators;

export const getSuggestedPostalCode = () => {
  const sdkPostCode = getCheckoutContext().sdk?.state.customer?.address?.postalCode;
  return {
    country: getCheckoutContext().sdk?.state.countryCode.toUpperCase(),
    postalCode: sdkPostCode || getCheckoutContext().suggestedPostalCode,
  };
};

export const getCheckoutSettings = () => getCheckoutContext().settings;

const saveUserCountry = (countryCode: string) => {
  return post('/api/set_user_country', { countryCode }, Type.Unknown());
};

let currentApiSdkVersionPromise: null | Promise<string> = null;
const checkApiSdkVersion = async (): Promise<string> => {
  if (currentApiSdkVersionPromise) {
    return currentApiSdkVersionPromise;
  }

  const newPromise = getText('/sdk-version');

  newPromise.finally(() => {
    currentApiSdkVersionPromise = null;
  });

  currentApiSdkVersionPromise = newPromise;

  return newPromise;
};

type CheckoutSdkParameters = {
  checkoutApiBaseUrl: string;
  checkoutPageUrl: string | undefined;
  orderConfirmationPageUrl: string | undefined;
  userCountryCode: string;
  locale: LocalizationContext;
  siteUrl: string;
  jwt: string;
  psp?: 'stripe' | 'adyen';
  atlasSessionId: string | undefined;
  translations: ReturnType<typeof getCommonTranslations>;
  manualOrderInfo?: { orderId: string; transactionId?: string };
};

const checkoutSdkParametersContext = 'checkout-sdk-parameters';
export const getCheckoutSdkParameters = (): CheckoutSdkParameters => {
  if (!hasContext(checkoutSdkParametersContext)) {
    throw new Error('No checkout sdk parameters found in context');
  }
  return getContext<CheckoutSdkParameters>(checkoutSdkParametersContext);
};

const prependSiteUrl = (url: string | undefined, siteUrl: string) => {
  if (!url) {
    return '';
  }
  return ensureNoTrailingSlash(siteUrl) + ensureLeadingSlash(url);
};

export const initializeCheckoutSdk = (checkoutSdkParameters: CheckoutSdkParameters) => {
  setContext(checkoutSdkParametersContext, checkoutSdkParameters);

  const {
    siteUrl,
    orderConfirmationPageUrl: dirtyOrderConfirmationPageUrl,
    checkoutPageUrl: dirtyCheckoutPageUrl,
    checkoutApiBaseUrl,
    userCountryCode,
    locale,
    jwt,
    atlasSessionId,
    translations,
    psp = 'adyen',
    manualOrderInfo,
  } = checkoutSdkParameters;
  // for both of these we first check the cookies, then localStorage, since we used to store the cookie in localStorage. Then we remove it from localStorage to avoid confusion in the future.
  // In july 2025 we can remove the localStorage (getItem, removeItem) and just use cookies.

  const checkoutPageUrl = prependSiteUrl(dirtyCheckoutPageUrl, siteUrl);
  const orderConfirmationPageUrl = prependSiteUrl(dirtyOrderConfirmationPageUrl, siteUrl);

  const currency = locale.currencyCode;
  const language = locale.language === 'default' ? 'us' : locale.language;

  const checkoutIdCookieKey = manualOrderInfo ? null : defaultCheckoutIdCookieKey;
  const checkoutVersionCookieKey = manualOrderInfo ? null : defaultCheckoutVersionCookieKey;

  const checkoutContextKey = manualOrderInfo ? JSON.stringify(manualOrderInfo) : 'default';

  const cartId =
    (checkoutIdCookieKey && getCookieFromDocument(document, checkoutIdCookieKey)) ?? undefined;

  const cartVersion =
    (checkoutVersionCookieKey && getCookieFromDocument(document, checkoutVersionCookieKey)) ??
    undefined;

  let countryCode = userCountryCode;

  const cartInstance = createCheckoutInstance({
    cartId,
    cartVersion,
    checkoutApiBaseUrl,
    countryCode: countryCode.toLowerCase(),
    currency,
    language,
    siteUrl,
    checkoutPageUrl,
    orderConfirmationPageUrl,
    tokenString: jwt,
    psp,
    refreshToken: async () => {
      const { token } = await post(
        '/api/auth/refresh-token',
        '',
        Type.Object({ token: Type.String() }),
      );
      return token;
    },
    onElementFocus: (element) => {
      element.scrollIntoView();
    },
    onServerSdkVersionMismatch: ({ currentSdkVersion, checkoutApiSdkVersion }) => {
      checkApiSdkVersion().then((a2SdkVersion) => {
        if (
          compareSdkVersions(currentSdkVersion, a2SdkVersion) === -1 &&
          compareSdkVersions(currentSdkVersion, checkoutApiSdkVersion) === -1
        ) {
          setGlobalError('sdkVersionMismatch');
        }
      });
    },
    onShowOrderConfirmation: (orderId) => {
      return goto(`${orderConfirmationPageUrl}?orderId=${encodeURIComponent(orderId)}`);
    },
    onUnexpectedError: (_error, args) => {
      setGlobalError(args?.errorType ?? 'general');
    },
    atlasSessionId: atlasSessionId ?? '',
    translateApiMessage: (errorCode) => {
      const translationKey = errorCode ? apiErrorToTranslationsMap[errorCode] : undefined;
      if (translationKey) {
        return translations[translationKey];
      }
      return undefined;
    },
    getTrackingCookies: () => {
      const cookiePairs = document.cookie
        .split('; ')
        .map((cookie) => {
          const [name, value = ''] = cookie.split('=');
          return [name, value] as const;
        })
        .filter((x) => x[0].startsWith('_ga'));
      return Object.fromEntries(cookiePairs);
    },
  });

  const cartEventsTracker = getEnhancedCommereceEventsTracker(() => cartInstance);

  const oldUpdateCountry = cartInstance.updateCountry;
  cartInstance.updateCountry = async (...args) => {
    const result = await oldUpdateCountry(...args);
    await loadCheckoutWithDefaults();
    return result;
  };

  const font = '"Noto Sans", Helvetica, Arial, sans-serif';
  const stripeLocale = toStripeLocale(language);

  cartInstance.state.paymentProviderElementsOptions.stripe = {
    elements: {
      locale: stripeLocale,
      appearance: {
        labels: 'floating',
        rules: {
          '.RadioIcon': {
            width: '32px',
          },
          '.RadioIconOuter': {
            stroke: colors.default['st-black'],
            strokeWidth: '5px',
          },
          '.RadioIconInner': {
            stroke: colors.default['st-white'],
            r: '26',
          },
          '.AccordionItem': {
            border: 'none',
            boxShadow: 'none',
            borderBottom: `1px solid ${colors.default['st-gray'][300]}`,
            paddingLeft: '0',
            paddingRight: '0',
            fontSize: '1rem',
          },
          '.AccordionItem:last-of-type': {
            borderBottom: 'none',
          },
        },
        variables: {
          colorDanger: colors.default['st-red'][500],
          fontFamily: font,
          colorText: colors.default['st-gray'][900],
          colorBackground: 'transparent',
          colorPrimary: colors.default['st-black'],
        },
      },
    },
    paymentElement: {
      layout: {
        type: 'accordion',
        defaultCollapsed: false,
        radios: true,
      },
    },
  };

  cartInstance.state.onChange((newState) => {
    if (countryCode !== newState.countryCode) {
      countryCode = newState.countryCode;
      saveUserCountry(countryCode);
    }
    cartEventsTracker.reportUpdatedLineItems(newState);
    const oldValue = checkoutContextStorage[checkoutContextKey].sdk;
    if (oldValue) {
      oldValue.state = {
        ...oldValue.state,
        ...newState,
      };
    }
    if (checkoutIdCookieKey && checkoutVersionCookieKey) {
      // Set cookies for cart ID and version instead of using localStorage
      const expirationDate = newState.isNew
        ? 0 // If it's a new cart, set cookie to expire immediately to clear it
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString(); // 30 days expiration
      const cookieOptions = `path=/; expires=${expirationDate}; SameSite=Strict; Secure`;

      document.cookie = `${checkoutIdCookieKey}=${newState.id}; ${cookieOptions}`;
      document.cookie = `${checkoutVersionCookieKey}=${newState.version}; ${cookieOptions}`;
    }
  });
  return { cartInstance, checkoutContextKey };
};

export function toStripeLocale(lang: string): StripeElementLocale {
  return lang === 'default' ? 'en' : (lang as StripeElementLocale);
}

type CartCommandError = Exclude<CartCommandResponse['errorCode'], undefined>;

const apiErrorToTranslationsMap: Record<
  CartCommandError,
  keyof ReturnType<typeof getCommonTranslations> | undefined
> = {
  QUANTITY_MUST_BE_GREATER_THAN_MINIMUM: 'quantityMustBeGreaterThanMinimum',
  CANNOT_BUY_GIFT_CARDS_WITH_A_GIFT_CARD: 'giftCardCannotBeUsedToBuyGiftCard',
  CART_HAS_ALREADY_BEEN_PAID_FOR: 'cartHasAlreadyBeenPaidFor',
  UNKNOWN_ERROR: 'anErrorHasOccured',
  INVALID_SHIPPING_METHOD: 'invalidShippingMethod',
  STICKER_PACK_NOT_FOUND: 'stickerPackNotFound',
  LINE_ITEM_NOT_FOUND: 'lineItemNotFound',
  ORDER_NOT_FOUND: 'orderNotFound',
  CODE_ALREADY_ADDED: 'codeAlreadyAdded',
  INVALID_CODE: 'invalidCode',
  GIFT_CARD_ALREADY_USED: 'giftCardAlreadyUsed',
  CODE_NOT_FOUND: 'codeNotFound',
  INVALID_VAT_NUMBER: 'invalidVatNumber',
  CATALOG_ITEM_NOT_FOUND: 'catalogItemNotFound',
  CATALOG_ITEM_ALREADY_IN_ANOTHER_CART: 'catalogItemAlreadyInAnotherCart',
  QUANTITY_MUST_BE_GREATER_THAN_0: 'quantityMustBeGreaterThan0',
  SHOP_ITEM_NOT_FOUND: 'shopItemNotFound',
  CUSTOM_STICKER_NOT_FOUND: 'customStickerNotFound',
  INVALID_PAYMENT_METHOD: 'invalidPaymentMethod',
  INVALID_EMAIL: 'invalidEmail',
  GIFT_CARDS_TEMPORARILY_DISABLED: 'giftCardsTemporarilyDisabled',
};
